import { useEffect, useState } from "react"
import { SITE_TIME_ZONE, addDays, todayInZone, zonedDayStart, type CalendarDate } from "./siteTime"

/**
 * How far past Pacific midnight the timer is set for: a timer can fire a hair early, and one that
 * fired at 11:59:59.999 would read the old day and wait another whole day to try again.
 */
const PAST_MIDNIGHT_MS = 1_000

/** Milliseconds from `now` to the next Pacific midnight. */
export function msUntilNextPacificMidnight(now: Date = new Date()): number {
  const tomorrow = addDays(todayInZone(SITE_TIME_ZONE, now), 1)
  return zonedDayStart(tomorrow, SITE_TIME_ZONE).getTime() - now.getTime()
}

/**
 * Today's Pacific date (`SITE_TIME_ZONE`), kept current while the calendar stays open (AB#821).
 *
 * Read once on render would leave a tab open past midnight calling yesterday Today, with "Go to
 * today" disabled. Instead the date is held in state and read again just after each Pacific
 * midnight, and whenever the page becomes visible again — a sleeping laptop or a background tab may
 * have let the timer fire late, or not at all.
 *
 * Only the marker moves: what the calendar shows stays the day it shows, and "Go to today", enabled
 * again, takes the user to the new one.
 */
export function usePacificToday(): CalendarDate {
  const [today, setToday] = useState(() => todayInZone(SITE_TIME_ZONE))

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined

    const refresh = () => {
      clearTimeout(timer)
      const now = new Date()
      setToday(todayInZone(SITE_TIME_ZONE, now))
      timer = setTimeout(refresh, msUntilNextPacificMidnight(now) + PAST_MIDNIGHT_MS)
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refresh()
    }

    refresh()
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () => {
      clearTimeout(timer)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [])

  return today
}
